import React, { useState } from 'react';
import apiClient from '../services/apiClient';

// 999.18844 — COPPA neutral age screen.
//
// Neutral by construction (FTC COPPA FAQ H.3): a date-of-birth input with NO
// default value and NO mention of the minimum age, so nothing coaches a child
// toward the "right" answer; a pre-checked "I am 13+" box is not an
// attestation and the backend refuses booleans. The server (auth-backend
// slices/age-gate) owns the rule — this file only collects the date.
//
// Back-button deterrent: after a refusal (AGE_REQUIREMENT_NOT_MET) a
// short-lived flag in localStorage makes every age screen on this browser show
// the refusal instead of the form until it expires. It stores no personal
// data (just an expiry instant).

export const AGE_GATE_BLOCK_KEY = 'raike_age_gate_block_until';
export const AGE_GATE_BLOCK_MS = 24 * 60 * 60 * 1000;
export const AGE_GATE_REFUSAL_MESSAGE = 'We are unable to create an account for you.';

export function isAgeGateBlocked(nowMs = Date.now()) {
  const raw = localStorage.getItem(AGE_GATE_BLOCK_KEY);
  if (!raw) return false;
  const until = Number(raw);
  if (!Number.isFinite(until) || until <= nowMs) {
    localStorage.removeItem(AGE_GATE_BLOCK_KEY);
    return false;
  }
  return true;
}

export function markAgeGateBlocked(nowMs = Date.now()) {
  localStorage.setItem(AGE_GATE_BLOCK_KEY, String(nowMs + AGE_GATE_BLOCK_MS));
}

/** True when an API error is the server's under-age refusal. */
export function isAgeRefusal(err) {
  return Boolean(err && err.data && err.data.code === 'AGE_REQUIREMENT_NOT_MET');
}

/**
 * True when an OAuth FIRST login must supply a date of birth before the server
 * creates the account (400). The 403 for an EXISTING account is ageAttestTokenOf.
 */
export function isAgeAttestationRequired(err) {
  return Boolean(err && err.status === 400 && err.data && err.data.code === 'AGE_ATTESTATION_REQUIRED');
}

/**
 * 999.18844 #5 — a login door (password, TOTP, Google, SSO) refuses to mint a
 * session for an account that never attested: 403 AGE_ATTESTATION_REQUIRED
 * with a short-lived age_attest `token`. Returns that token, or null for any
 * other error (a 403 with no token — e.g. the refresh path — is NOT a prompt).
 */
export function ageAttestTokenOf(err) {
  if (!err || err.status !== 403 || !err.data || err.data.code !== 'AGE_ATTESTATION_REQUIRED') return null;
  const { token } = err.data;
  return typeof token === 'string' && token !== '' ? token : null;
}

/**
 * POST the date of birth, authenticated ONLY by the age_attest token (never the
 * session token). Resolves to the new session ({ user, tokens }) when the age
 * is at/over the floor; rejects with the server's error otherwise.
 */
export function postAgeAttestation(token, birthDate) {
  return apiClient.post('/api/auth/age-attestation', { birthDate }, { authToken: token });
}

/**
 * Store the session pair the attestation returned, the same way the login
 * doors do. A response without both tokens is an error, never a half session.
 */
export function storeAttestedSession(data) {
  const tokens = data && data.tokens;
  if (!tokens || typeof tokens.accessToken !== 'string' || typeof tokens.refreshToken !== 'string' || !data.user) {
    throw new Error('Age attestation response carried no session');
  }
  apiClient.setTokens(tokens.accessToken, tokens.refreshToken);
  localStorage.setItem('auth_user', JSON.stringify(data.user));
}

export function AgeGateRefusal() {
  return <div className="error-message" role="alert">{AGE_GATE_REFUSAL_MESSAGE}</div>;
}

/** The bare date-of-birth field, for embedding in a larger form (RegisterPage). */
export function BirthDateField({ value, onChange, id = 'age-gate-birth-date' }) {
  return (
    <div className="form-group">
      <label htmlFor={id}>Date of birth</label>
      <input
        id={id}
        type="date"
        value={value}
        onChange={e => onChange(e.target.value)}
        required
        autoComplete="bday"
      />
    </div>
  );
}

/**
 * Stand-alone age screen. Calls onSubmit(birthDate) with the raw 'YYYY-MM-DD'.
 * `context` is required: 'registration' (OAuth first login — a NEW account)
 * honours the 24h refusal flag; 'existing' (the one-time prompt for an account
 * that already exists, maybe another person on a shared browser) always shows
 * the form — the server decides that attestation.
 */
export default function AgeGatePrompt({ context, onSubmit, submitting = false, error = '' }) {
  const [birthDate, setBirthDate] = useState('');

  if (context !== 'registration' && context !== 'existing') {
    throw new Error(`AgeGatePrompt: context must be 'registration' or 'existing', got ${JSON.stringify(context)}`);
  }
  if (context === 'registration' && isAgeGateBlocked()) return <AgeGateRefusal />;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(birthDate);
  };

  return (
    <form onSubmit={handleSubmit}>
      <p className="subtitle">Please enter your date of birth to continue.</p>
      {error && <div className="error-message" role="alert">{error}</div>}
      <BirthDateField value={birthDate} onChange={setBirthDate} />
      <button type="submit" className="btn btn-primary" disabled={submitting || !birthDate}>
        {submitting ? 'Continuing...' : 'Continue'}
      </button>
    </form>
  );
}
